import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  MessageSquare, Filter, Search, Bug, Lightbulb, HelpCircle, ThumbsUp,
  Clock, CheckCircle2, AlertCircle, Eye, Trash2, X
} from 'lucide-react';
import Button from './ui/Button';

const FeedbackAdmin = ({ user }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'feedback'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setFeedbacks(data);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = [...feedbacks];

    // Filter par type
    if (filterType !== 'all') {
      filtered = filtered.filter(f => f.type === filterType);
    }

    // Filter par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(f => (f.status || 'new') === filterStatus);
    }

    // Filter par priorité
    if (filterPriority !== 'all') {
      filtered = filtered.filter(f => f.priority === filterPriority);
    }

    // Search
    if (searchTerm) {
      filtered = filtered.filter(f => 
        f.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredFeedbacks(filtered);
  }, [feedbacks, filterType, filterStatus, filterPriority, searchTerm]);

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), {
        status: newStatus,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Erreur changement statut:', error);
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'bug': return <Bug className="text-red-500" size={20}/>;
      case 'feature': return <Lightbulb className="text-blue-500" size={20}/>;
      case 'question': return <HelpCircle className="text-amber-500" size={20}/>;
      case 'praise': return <ThumbsUp className="text-green-500" size={20}/>;
      default: return <MessageSquare className="text-slate-500" size={20}/>;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'bug': return 'Bug';
      case 'feature': return 'Amélioration';
      case 'question': return 'Question';
      case 'praise': return 'Compliment';
      default: return type;
    }
  };

  const getStatusBadge = (status) => {
    const s = status || 'new';
    switch(s) {
      case 'new':
        return <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">Nouveau</span>;
      case 'in-progress':
        return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">En cours</span>;
      case 'resolved':
        return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">Résolu</span>;
      default:
        return <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold">{s}</span>;
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'low': return 'text-slate-600';
      case 'medium': return 'text-blue-600';
      case 'high': return 'text-orange-600';
      case 'critical': return 'text-red-600 font-bold';
      default: return 'text-slate-500';
    }
  };

  const stats = {
    total: feedbacks.length,
    new: feedbacks.filter(f => (f.status || 'new') === 'new').length,
    bugs: feedbacks.filter(f => f.type === 'bug').length,
    features: feedbacks.filter(f => f.type === 'feature').length
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 animate-page pb-24">
      
      {/* Header avec stats */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gradient-to-br from-orange-500 to-pink-500 p-3 rounded-xl">
            <MessageSquare className="text-white" size={28}/>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-slate-800">Feedbacks Bêta</h2>
            <p className="text-slate-500">Retours des testeurs</p>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border-2 border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Total</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 border-2 border-orange-200 shadow-sm">
            <div className="text-2xl font-bold text-orange-600">{stats.new}</div>
            <div className="text-xs text-orange-600 uppercase tracking-wide font-bold">Nouveaux</div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border-2 border-red-200 shadow-sm">
            <div className="text-2xl font-bold text-red-600">{stats.bugs}</div>
            <div className="text-xs text-red-600 uppercase tracking-wide font-bold">Bugs</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200 shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{stats.features}</div>
            <div className="text-xs text-blue-600 uppercase tracking-wide font-bold">Améliorations</div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20}/>
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          {/* Type filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium"
          >
            <option value="all">Tous les types</option>
            <option value="bug">🐛 Bugs</option>
            <option value="feature">💡 Améliorations</option>
            <option value="question">❓ Questions</option>
            <option value="praise">👍 Compliments</option>
          </select>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium"
          >
            <option value="all">Tous statuts</option>
            <option value="new">🆕 Nouveaux</option>
            <option value="in-progress">⏳ En cours</option>
            <option value="resolved">✅ Résolus</option>
          </select>

          {/* Priority filter */}
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium"
          >
            <option value="all">Toutes priorités</option>
            <option value="critical">🔴 Critique</option>
            <option value="high">🟠 Haute</option>
            <option value="medium">🔵 Moyenne</option>
            <option value="low">⚪ Basse</option>
          </select>
        </div>
      </div>

      {/* Liste des feedbacks */}
      <div className="space-y-4">
        {filteredFeedbacks.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
            <MessageSquare size={48} className="text-slate-300 mx-auto mb-4"/>
            <p className="text-slate-500 font-medium">Aucun feedback trouvé</p>
          </div>
        ) : (
          filteredFeedbacks.map(feedback => (
            <div key={feedback.id} className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 hover:shadow-md transition">
              <div className="flex items-start gap-4">
                
                {/* Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getTypeIcon(feedback.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-slate-800 text-lg">{feedback.subject || 'Sans titre'}</h3>
                        {getStatusBadge(feedback.status)}
                        <span className="text-xs text-slate-500">{getTypeLabel(feedback.type)}</span>
                        {feedback.priority && feedback.type === 'bug' && (
                          <span className={`text-xs font-bold uppercase ${getPriorityColor(feedback.priority)}`}>
                            {feedback.priority}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{feedback.message}</p>
                    </div>
                    <button
                      onClick={() => setSelectedFeedback(feedback)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition flex-shrink-0"
                    >
                      <Eye size={18} className="text-slate-400"/>
                    </button>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>👤 {feedback.userEmail}</span>
                    <span>🕐 {feedback.createdAt?.toLocaleDateString('fr-FR')} à {feedback.createdAt?.toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</span>
                  </div>

                  {/* Quick actions */}
                  <div className="flex gap-2 mt-3">
                    {feedback.status !== 'new' && (
                      <button
                        onClick={() => handleStatusChange(feedback.id, 'new')}
                        className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 font-medium transition"
                      >
                        → Nouveau
                      </button>
                    )}
                    {feedback.status !== 'in-progress' && (
                      <button
                        onClick={() => handleStatusChange(feedback.id, 'in-progress')}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium transition"
                      >
                        → En cours
                      </button>
                    )}
                    {feedback.status !== 'resolved' && (
                      <button
                        onClick={() => handleStatusChange(feedback.id, 'resolved')}
                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium transition"
                      >
                        → Résolu
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal détail */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedFeedback(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-slate-800 to-slate-900 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  {getTypeIcon(selectedFeedback.type)}
                  <div>
                    <h3 className="text-xl font-bold mb-1">{selectedFeedback.subject || 'Sans titre'}</h3>
                    <p className="text-slate-300 text-sm">{getTypeLabel(selectedFeedback.type)}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedFeedback(null)} className="p-2 hover:bg-white/20 rounded-lg transition">
                  <X size={24}/>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              
              {/* Meta info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-xs text-slate-500 mb-1">Statut</div>
                  {getStatusBadge(selectedFeedback.status)}
                </div>
                {selectedFeedback.priority && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="text-xs text-slate-500 mb-1">Priorité</div>
                    <span className={`font-bold uppercase text-sm ${getPriorityColor(selectedFeedback.priority)}`}>
                      {selectedFeedback.priority}
                    </span>
                  </div>
                )}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-xs text-slate-500 mb-1">Utilisateur</div>
                  <div className="text-sm font-medium text-slate-800">{selectedFeedback.userEmail}</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-xs text-slate-500 mb-1">Date</div>
                  <div className="text-sm font-medium text-slate-800">
                    {selectedFeedback.createdAt?.toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <h4 className="font-bold text-slate-800 mb-2">Message</h4>
                <div className="bg-slate-50 rounded-xl p-4 whitespace-pre-wrap text-slate-700">
                  {selectedFeedback.message}
                </div>
              </div>

              {/* Technical info */}
              <details className="bg-slate-50 rounded-xl p-4">
                <summary className="cursor-pointer font-medium text-slate-700 text-sm">Informations techniques</summary>
                <div className="mt-3 space-y-2 text-xs text-slate-600">
                  <div><strong>URL:</strong> {selectedFeedback.url}</div>
                  <div><strong>User Agent:</strong> {selectedFeedback.userAgent}</div>
                  <div><strong>ID:</strong> {selectedFeedback.id}</div>
                </div>
              </details>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => handleStatusChange(selectedFeedback.id, 'in-progress')}
                  variant="secondary"
                  className="flex-1"
                >
                  <Clock size={18}/>
                  En cours
                </Button>
                <Button
                  onClick={() => handleStatusChange(selectedFeedback.id, 'resolved')}
                  className="flex-1"
                >
                  <CheckCircle2 size={18}/>
                  Résolu
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FeedbackAdmin;
